import { useMemo } from 'react';
import { transform } from 'next/dist/build/swc';
import { useRouter } from 'next/navigation';
import { ArrowRightRounded } from '@mui/icons-material';
import { Button, Stack, Typography } from '@mui/material';
import type { AppStorePostFilter, PostWordType } from '@/interfaces/service/appstore/appstore.interface';
import { useAppStoreStore } from '@/stores/appstore.store';
import { REVIEW_ROUTES } from '@/constants/path.constants';
import { sortByProperty } from '@/utils/array.util';
import FlexBox from '@/components/FlexBox';
import { glitterEffectStyles } from '@/app/stylesguides';
import theme from '@/app/theme';

const GraphBar = ({ word, count, totalCount, barColorRGBs, opacity, onClick }: { word: string; count: number; totalCount: number; barColorRGBs: number[]; opacity: number; onClick: () => void }) => {
  const rate = Math.round((count / totalCount) * 100);
  return (
    <Button
      sx={{
        width: '100%',
        display: 'flex',
        borderRadius: '6px',
        background: `linear-gradient(to right, rgba(${barColorRGBs.join(', ')}, ${opacity}) 0 ${rate}%, ${theme.palette.gray[50]} ${rate}% 100%)`,
        height: '36px',
        padding: '6px 24px',
        justifyContent: 'space-between',
        ...glitterEffectStyles,
        ':hover': {
          background: `linear-gradient(to right, rgba(${barColorRGBs.join(', ')}, ${opacity + 0.3 >= 1 ? 1 : opacity + 0.3}) 0 ${rate}%, ${theme.palette.gray[50]} ${rate}% 100%)`,
          transform: 'scale(1.02)'
        }
      }}
      onClick={onClick}
    >
      <Typography component="span" color={theme.palette.gray[900]} fontWeight={600} variant="subtitle2">
        #{word}
      </Typography>
      <FlexBox>
        <Typography component="span" color={theme.palette.gray[900]} fontWeight={600} variant="body1">
          {count}
        </Typography>
        <ArrowRightRounded sx={{ color: theme.palette.success.main }} />
      </FlexBox>
    </Button>
  );
};

const PostWordGraph = ({
  appStoreId,
  wordData
}: {
  appStoreId: number;
  wordData: {
    POSITIVE: { word: string; count: number }[];
    NEGATIVE: { word: string; count: number }[];
  };
}) => {
  const router = useRouter();

  const { setAppStorePostsFilters } = useAppStoreStore((state) => state);

  const positiveWords = useMemo(() => wordData.POSITIVE?.sort(sortByProperty('count')).slice(0, wordData.POSITIVE.length >= 3 ? 3 : wordData.POSITIVE.length), [wordData]);

  const positiveTotalCount = positiveWords.reduce((total, item) => total + item.count, 0);
  const negativeWords = useMemo(() => wordData.NEGATIVE?.sort(sortByProperty('count')).slice(0, wordData.NEGATIVE.length >= 3 ? 3 : wordData.POSITIVE.length), [wordData]);
  const negativeTotalCount = negativeWords.reduce((total, item) => total + item.count, 0);

  const goToReviewsPageWithWordFilter = (word: string, type: PostWordType) => {
    const filter: AppStorePostFilter = {
      [type === 'POSITIVE' ? 'positiveWordList' : 'negativeWordList']: [word]
    };

    setAppStorePostsFilters(appStoreId, filter);
    router.push(REVIEW_ROUTES.getReviews(appStoreId));
  };

  return (
    <Stack gap="10px" sx={{ width: '100%' }}>
      {positiveWords.map(({ word, count }, i) => (
        <GraphBar
          key={word}
          word={word}
          count={count}
          totalCount={positiveTotalCount}
          barColorRGBs={[52, 73, 255]}
          opacity={0.3 - i * 0.07}
          onClick={() => goToReviewsPageWithWordFilter(word, 'POSITIVE')}
        />
      ))}
      {negativeWords.map(({ word, count }, i) => (
        <GraphBar
          key={word}
          word={word}
          count={count}
          totalCount={negativeTotalCount}
          barColorRGBs={[255, 79, 23]}
          opacity={0.3 - i * 0.07}
          onClick={() => goToReviewsPageWithWordFilter(word, 'NEGATIVE')}
        />
      ))}
    </Stack>
  );
};

export default PostWordGraph;
